import { forwardRef, useEffect, useMemo, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { MDBRow, MDBCol } from "mdbreact";

import "./C25Measurements.scss"

import { Button, Label, TextArea } from "abs-ui-components";

import { AUTOSAVE_SUCCESS_MESSAGE, c42Fields } from "../../Constants";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import AutoSave from "../../../../../common/AutoSave/AutoSave";

const C25Measurements = forwardRef(({ c25RegardingMeasurements, vesselMstId,refetchMonitoringData }, ref) => {
    const { control, getValues, reset, formState } = useFormContext();
    
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);

    const { dirtyFields } = formState;
    const isC25Dirty = !!(dirtyFields?.c25 && Object.keys(dirtyFields.c25).length > 0);

    useEffect(() => {
        if (c25RegardingMeasurements) {
            reset(prev => ({ ...prev, c25: c25RegardingMeasurements }));
        }
    }, [c25RegardingMeasurements, reset]);

    const c25Fields = useMemo(() => {
        return c42Fields.reduce((acc, field) => {
            if (field.name === "dataSources") return acc;
            if (field.name === "descriptionOfProcedure") {
                acc.push({
                    ...field,
                    label:
                        "Description of procedure (a brief description of the procedure can be provided if already existing outside the monitoring plan)",
                });
            } else {
                acc.push(field);
            }
            return acc;
        }, []);
    }, []);

    AutoSave(ref, [], async () => {
        if (!isC25Dirty) return;
        await handleSave(true);
    });

    const handleSave = async (isAuto = false) => {
        const formValues = getValues("c25");
        const payload = {
            ...c25Fields.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        try {
            const C23BunkeringQuantity = await callApi({
                method: "POST",
                url: API_ROUTES.C25(vesselMstId),
                data: payload,
            });
            if (C23BunkeringQuantity?.isSuccess) {
                emitToasterMsg("success", "Table C.2.5", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, c25: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.2.5", C23BunkeringQuantity?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.2.5", error?.errorMessage);
        }
    };

    return (
        <div className="b7-section-form mt-4">
            <MDBRow className="mb-2">
                <MDBCol md="4">
                    <h6 className="titles-font">Title of procedure</h6>
                </MDBCol>
                <MDBCol md="7">
                    <h6 className="titles-font">Recording, retrieving, transmitting and storing information regarding measurements</h6>
                </MDBCol>
            </MDBRow>

            {c25Fields.map(({ label, name, maxLength }, index) => (
                <MDBRow className="align-items-center mb-3" key={index}>
                    <MDBCol md="4">
                        <Label name={label} bold />
                    </MDBCol>
                    <MDBCol md="4">
                        <Controller
                            name={`c25.${name}`}
                            control={control}
                            render={({ field }) => (
                                <TextArea
                                    id={`c25_${name}`}
                                    className="textarea-resize"
                                    value={field?.value || ""}
                                    onChange={field.onChange}
                                    placeholder=""
                                    maxLength={maxLength}
                                    rows={1}
                                />
                            )}
                        />
                    </MDBCol>
                </MDBRow>
            ))}

            <div className="text-center">
                <Button id="c25_save_btn"
                    disabled={!isC25Dirty}
                    onClick={async () => {
                        manualSaveTriggered.current = true;
                        await handleSave(false);
                    }}
                >Save</Button>
            </div>
        </div>
    );
});

export default C25Measurements;
